'use client';

import { captureBrowserAnalytics } from '@/reporting/reliability/browserAnalytics';
import { withSpmPageRefer } from '@/reporting/spm/spm-session';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import {
  canTrackPageLeave,
  createPageLeavePropertiesOnce,
  resetPageLeaveState,
  type PageLeaveSnapshotSource,
  type PageLeaveState,
  type PageLeaveTrigger,
} from './pageLeaveTrackingState';

/** 读取创建 PageLeave 快照所需的同一浏览器状态。 */
function getBrowserPageLeaveSource(): PageLeaveSnapshotSource | null {
  // SSR 或文档未就绪时不访问浏览器全局。
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;

  return {
    location: window.location,
    document,
    now: Date.now,
  };
}

/** 发送普通 PageLeave 业务事件，并保持旧 pathname/beforeunload once 语义。 */
export function useAnalyticsPageLeaveTracking(): void {
  const pathname = usePathname();
  const stateRef = useRef<PageLeaveState>({ snapshot: null });
  const lastPathnameRef = useRef<string | null>(null);

  /** 从当前快照生成 properties，并同步接纳原生 generic 输入。 */
  const sendPageLeave = useCallback((trigger: PageLeaveTrigger, nextUrlPath?: string): void => {
    // App H5 与 SSR 环境不生成或发送 Web PageLeave。
    if (!canTrackPageLeave()) return;

    const source = getBrowserPageLeaveSource();

    // 浏览器数据源未就绪时安全跳过。
    if (!source) return;

    const snapshot = stateRef.current.snapshot;
    let attemptedSnapshot = snapshot;

    try {
      const properties = createPageLeavePropertiesOnce(
        stateRef.current,
        trigger,
        source,
        nextUrlPath,
      );

      attemptedSnapshot = stateRef.current.snapshot;

      // 同一页面已接纳时不再重复捕获。
      if (!properties) return;

      // 保存发生时已计算的时长与属性，内部触发时间由捕获层冻结。
      const accepted = captureBrowserAnalytics(
        { kind: 'track', eventName: 'PageLeave', properties: withSpmPageRefer(properties) },
        trigger === 'pathname_change' ? {} : { delivery: 'immediate' },
      );

      // 未接纳不会产生交付记录，保留下一次真实离开信号的重试资格。
      if (!accepted && snapshot && stateRef.current.snapshot === attemptedSnapshot) {
        stateRef.current.snapshot = snapshot;
      }
    } catch {
      // 接纳前异常释放当前快照；已接纳记录的异步终态只由队列管理。
      if (snapshot && stateRef.current.snapshot === attemptedSnapshot) {
        stateRef.current.snapshot = snapshot;
      }
      // SDK 或浏览器读取异常不影响导航和卸载主链路。
    }
  }, []);

  // 首次只保存快照；仅 pathname 变化时发送上一页并重置下一页。
  useEffect(() => {
    if (!canTrackPageLeave()) return;

    const source = getBrowserPageLeaveSource();

    // 浏览器数据源未就绪时不修改状态。
    if (!source) return;

    // 首次挂载只记录进入页快照。
    if (!stateRef.current.snapshot) {
      resetPageLeaveState(stateRef.current, source);
      lastPathnameRef.current = pathname;
      return;
    }

    // 兼容快照先由 beforeunload effect 初始化的执行顺序。
    if (lastPathnameRef.current === null) {
      lastPathnameRef.current = pathname;
      return;
    }

    // query-only 或同 pathname 重渲染不属于页面离开。
    if (lastPathnameRef.current === pathname) return;

    sendPageLeave('pathname_change', pathname);

    const nextSource = getBrowserPageLeaveSource();

    // 为新 pathname 创建独立的一次性快照。
    if (nextSource) {
      resetPageLeaveState(stateRef.current, nextSource);
    }

    lastPathnameRef.current = pathname;
  }, [pathname, sendPageLeave]);

  // pagehide 与 beforeunload 共用once快照；切后台本身不结束本页停留。
  useEffect(() => {
    if (!canTrackPageLeave()) return;

    // 防御 route effect 尚未创建首屏快照的极端执行顺序。
    if (!stateRef.current.snapshot) {
      const source = getBrowserPageLeaveSource();

      // 数据源有效时才创建首屏快照。
      if (source) {
        resetPageLeaveState(stateRef.current, source);
      }
    }

    /** 浏览器卸载前尝试发送当前页面离开。 */
    const handleBeforeUnload = (): void => {
      sendPageLeave('beforeunload');
    };
    const handlePageHide = (): void => {
      sendPageLeave('pagehide');
    };
    /** BFCache恢复创建新访问快照，停留时长不包含被冻结的时间。 */
    const handlePageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) return;
      const source = getBrowserPageLeaveSource();
      if (source) resetPageLeaveState(stateRef.current, source);
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pageshow', handlePageShow);

    /** 组件 cleanup 仅释放浏览器监听。 */
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [sendPageLeave]);
}
